/**
 * useCypSchoolRepStatus
 * =====================
 *
 * Loads the nominated school representative's details and questionnaire
 * progress for a CYP ADHD parent/carer (the `legalGuardian` informant).
 *
 * Why a callable rather than a Firestore read
 * -------------------------------------------
 * Firestore rules let an informant read only their *own* profile and forms.
 * The school rep is a different informant, and the child's patient document
 * is off limits too, so the parent's browser cannot assemble this view
 * itself. The `getCypSchoolRepStatus` Cloud Function reads on the parent's
 * behalf after checking that the caller is a guardian and that the school
 * rep is the one they nominated. See
 * python-functions/functions/cyp_school_rep_status.py for the response shape.
 *
 * This mirrors how `useInformantManagement` fetches data on mount for adult
 * pathways: a short loading state, then data, with a `refresh()` for after
 * an action (e.g. resending the invite) so the card reflects the new state.
 *
 * Gating
 * ------
 * The callable only fires when `enabled` is true. The dashboard passes
 * `isCypGuardian && nominationStatus === 'nominated'`, so autism/ADHD
 * informants, school reps, and guardians who skipped or have not yet
 * nominated never make the call. For those callers the hook settles
 * immediately with `status: null`, `schoolRep: null`, `loading: false`.
 *
 * Return shape
 * ------------
 *   {
 *     status:    'none' | 'pending' | 'skipped' | 'nominated' | null,
 *     schoolRep: {...} | null,       // only when status === 'nominated'
 *     loading:   boolean,            // true during the first load AND refreshes
 *     error:     string | null,      // user-safe message when the call failed
 *     refresh:   () => Promise<void>
 *   }
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

export const SCHOOL_REP_STATUS_ERROR_MESSAGE =
  'We could not load the school representative details. Please try again.';

/**
 * @param {Object}  options
 * @param {boolean} options.enabled - Fire the callable only when true
 */
export default function useCypSchoolRepStatus({ enabled }) {
  const [status, setStatus] = useState(null);
  const [schoolRep, setSchoolRep] = useState(null);
  const [loading, setLoading] = useState(Boolean(enabled));
  const [error, setError] = useState(null);

  // Guard against setting state after unmount (the dashboard can navigate
  // away mid-request) and against an older request resolving after a newer
  // refresh and overwriting fresher data.
  const mountedRef = useRef(true);
  const requestIdRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const load = useCallback(async () => {
    if (!enabled) return;
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const call = httpsCallable(functions, 'getCypSchoolRepStatus');
      const result = await call({});
      if (!mountedRef.current || requestId !== requestIdRef.current) return;
      const data = result?.data || {};
      setStatus(typeof data.status === 'string' ? data.status : 'none');
      setSchoolRep(data.status === 'nominated' && data.schoolRep ? data.schoolRep : null);
    } catch (err) {
      if (!mountedRef.current || requestId !== requestIdRef.current) return;
      console.error('useCypSchoolRepStatus: failed to load school representative status', err);
      setError(SCHOOL_REP_STATUS_ERROR_MESSAGE);
    } finally {
      if (mountedRef.current && requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      // Reset so a guardian who just skipped (enabled flips true -> false)
      // does not keep showing a stale card.
      setStatus(null);
      setSchoolRep(null);
      setLoading(false);
      setError(null);
      return;
    }
    load();
  }, [enabled, load]);

  return { status, schoolRep, loading, error, refresh: load };
}
